import { SITE_URL } from '../config/runtime';

export function slugifyReportTitle(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-')
    .slice(0, 80) || 'report';
}

export function normalizePublicReportPath(value) {
  const normalized = String(value || '').trim().replace(/[?#].*$/, '').replace(/\/+$/, '');
  return normalized.startsWith('/reports/') ? (normalized || '/') : '';
}

export function buildPublicReportPath(id, slugOrTitle = '') {
  const safeId = String(id || '').trim();
  const slug = slugifyReportTitle(slugOrTitle);
  return safeId ? `/reports/${safeId}/${slug}` : '/reports';
}

export function buildPublicReportUrl(id, slugOrTitle = '', origin = '') {
  const baseOrigin = String(origin || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');
  const path = buildPublicReportPath(id, slugOrTitle);
  return baseOrigin ? `${baseOrigin}${path}` : path;
}

export function buildPublicReportAppPath(id, slugOrTitle = '') {
  const reportPath = buildPublicReportPath(id, slugOrTitle);
  return reportPath === '/reports' ? '/' : `/?public_report=${encodeURIComponent(reportPath)}`;
}

export function buildPublicReportAppUrl(id, slugOrTitle = '', origin = '') {
  const baseOrigin = String(origin || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');
  const path = buildPublicReportAppPath(id, slugOrTitle);
  return baseOrigin ? `${baseOrigin}${path}` : path;
}

export function buildPublicReportOgPath(id, slugOrTitle = '') {
  const path = buildPublicReportPath(id, slugOrTitle);
  return path === '/reports' ? '/reports/og-image.svg' : `${path}/og-image.svg`;
}

export function buildPublicReportOgUrl(id, slugOrTitle = '', origin = '') {
  const baseOrigin = String(origin || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/$/, '');
  const path = buildPublicReportOgPath(id, slugOrTitle);
  return baseOrigin ? `${baseOrigin}${path}` : path;
}

export function buildPublicReportHtmlPath(id) {
  const safeId = String(id || '').trim();
  return safeId ? `/experiments/${safeId}/html` : '/experiments';
}

export function buildPublicReportHtmlUrl(id, apiBase = '') {
  const base = String(apiBase || '').replace(/\/$/, '');
  const path = buildPublicReportHtmlPath(id);
  return base ? `${base}${path}` : path;
}

export function getReportDescription(exp) {
  if (exp?.description) return String(exp.description).trim().slice(0, 240);
  const dataset = exp?.feed_summary?.dataset || exp?.snapshot?.dataset || {};
  const parts = [
    exp?.type ? String(exp.type).replace(/_/g, ' ') : '',
    dataset?.name || '',
    dataset?.task ? `${dataset.task} task` : '',
    dataset?.rows ? `${Number(dataset.rows).toLocaleString()} rows` : '',
    dataset?.cols ? `${dataset.cols} columns` : '',
  ].filter(Boolean);
  return parts.join(' | ').slice(0, 240) || 'Public Jorvex analysis report.';
}

export function getReportAuthorLabel(exp) {
  return exp?.display_name || exp?.username || exp?.author || 'Researcher';
}

export function buildPublicReportStructuredData(exp, canonicalUrl) {
  const dataset = exp?.feed_summary?.dataset || exp?.snapshot?.dataset || {};
  const slug = exp?.slug || exp?.title || '';
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: exp?.title || 'Jorvex Public Report',
    description: getReportDescription(exp),
    url: canonicalUrl,
    image: exp?.id ? buildPublicReportOgUrl(exp.id, slug, originOrFallback(canonicalUrl)) : undefined,
    mainEntityOfPage: canonicalUrl,
    datePublished: exp?.created_at || exp?.createdAt || undefined,
    dateModified: exp?.updated_at || exp?.updatedAt || exp?.created_at || exp?.createdAt || undefined,
    author: {
      '@type': 'Person',
      name: getReportAuthorLabel(exp),
    },
    publisher: {
      '@type': 'Organization',
      name: 'Jorvex',
      url: originOrFallback(canonicalUrl),
    },
    about: [dataset?.name, dataset?.task, exp?.type].filter(Boolean).join(' | '),
    keywords: Array.isArray(exp?.tags) ? exp.tags.join(', ') : undefined,
  };
}

function originOrFallback(url) {
  try {
    return new URL(url).origin;
  } catch (_) {
    return SITE_URL;
  }
}
